import { DealHeaderV2 } from "@/components/deal-detail/deal-header-v2";
import { DealRightPanel } from "@/components/deal-detail/deal-right-panel";
import { DealTabBar, type DealTab } from "@/components/deal-detail/deal-tab-bar";
import { ReportPanelViewer } from "@/components/deal-detail/report-panel-viewer";
import { DeleteDialog } from "@/components/deals/delete-dialog";
import { FounderInvitationDialog } from "@/components/deals/founder-invitation-dialog";
import { DiligencegptSidebar } from "@/components/sidebar/diligencegpt-sidebar";
import type { DealTransformed } from "@/lib/schema/deal-schema";
import { cn } from "@/lib/utils";
import { useLayoutStore } from "@/store/layout-store";
import { useWorkspaceStore } from "@/store/workspace-store";
import { ChevronRight, X } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useLocation, useOutletContext, useSearchParams } from "react-router";

// Lazy tab imports — these wrap existing components
import ProfileTab, { getProfilePendingCount } from "@/components/deal-detail/tabs/profile-tab";
import AnalysisTab from "@/components/deal-detail/tabs/analysis-tab";
import AnalysisDocumentsTab from "@/components/deal-detail/tabs/analysis-documents-tab";
import ReportsTab from "@/components/deal-detail/tabs/reports-tab";
import DataRoomTab from "@/components/deal-detail/tabs/data-room-tab";
import EmailsTab from "@/components/deal-detail/tabs/emails-tab";
import NotesTab from "@/components/deal-detail/tabs/notes-tab";
import PortcoTab from "@/components/deal-detail/tabs/portco-tab";

interface DealDetailOutletContext {
  isDealsLoading: boolean;
  chatOpen: boolean;
  setChatOpen: (value: boolean) => void;
  currentDeal: DealTransformed;
  reportStatus: unknown;
  reportData: unknown[];
  invitationStatus: {
    hasInvitation: boolean;
    invitation: unknown;
    companyName: string;
  } | null;
  refetchInvitationStatus: () => void;
  onDealDeleted: () => void;
}

export default function DealDetailPage() {
  const {
    currentDeal,
    reportStatus,
    invitationStatus,
    refetchInvitationStatus,
    onDealDeleted,
  } = useOutletContext<DealDetailOutletContext>();
  const { activeWorkspaceId } = useWorkspaceStore();

  const [searchParams] = useSearchParams();
  const location = useLocation();

  // Tab state — read from URL or default to "profile"
  const initialTab = (searchParams.get("tab") as DealTab) || "profile";
  const [activeTab, setActiveTab] = useState<DealTab>(initialTab);
  /**
   * The analysis document currently open, as text, so the chat can answer
   * questions about it. Held here rather than in the tab because the chat lives
   * in a sibling panel. Null whenever no document is open.
   */
  const [analysisContext, setAnalysisContext] = useState<string | null>(null);
  // A finished agent report chosen in the Reports tab, for the Agents tab to open.
  const [agentReportRequest, setAgentReportRequest] = useState<{
    templateId: string;
    at: number;
  } | null>(null);

  // Sync IN from a React Router navigation so external links (?tab=…)
  // still flip the tab. Keyed on `location` (new identity per navigation),
  // not `searchParams`: tab clicks update the URL via history.replaceState
  // (see handleTabChange) without notifying React Router, so a later
  // navigate() to a search string the router already holds (e.g. clicking
  // a report's "Upload Documents" twice) wouldn't change the memoised
  // `searchParams` and the sync would be skipped. `activeTab` is omitted
  // from deps because tab clicks don't touch the router — including it
  // would fire this effect on every click, read the stale router-tracked
  // search, and revert the click. INVARIANT: if handleTabChange ever
  // switches back to setSearchParams, restore activeTab to deps + an
  // inequality check or this turns into an infinite ping-pong.
  useEffect(() => {
    const tabFromUrl =
      (new URLSearchParams(location.search).get("tab") as DealTab) || "profile";
    setActiveTab(tabFromUrl);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  // Header action dialogs
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);

  // Right panel state
  const [rightPanelOpen, setRightPanelOpen] = useState(false);
  const [rightPanelWidth, setRightPanelWidth] = useState(50);
  const [rightPanelContent, setRightPanelContent] = useState<
    null | "chat" | { type: string; title: string; versionId?: string }
  >(null);

  // Fullscreen state lives in a small global store so ancestor layouts
  // (BaseLayout's AppSidebar) can react to it without prop-drilling.
  const isReportPanelFullscreen = useLayoutStore(
    (s) => s.isReportPanelFullscreen
  );
  const setReportPanelFullscreen = useLayoutStore(
    (s) => s.setReportPanelFullscreen
  );
  const toggleReportPanelFullscreen = useLayoutStore(
    (s) => s.toggleReportPanelFullscreen
  );

  const exitFullscreen = useCallback(() => {
    setReportPanelFullscreen(false);
  }, [setReportPanelFullscreen]);

  // Reset fullscreen when this page unmounts so navigating away never
  // leaves the sidebar hidden elsewhere in the app.
  useEffect(() => {
    return () => {
      setReportPanelFullscreen(false);
    };
  }, [setReportPanelFullscreen]);

  const activeReportType =
    rightPanelContent && typeof rightPanelContent === "object"
      ? rightPanelContent.type
      : null;
  // Any report-type panel is eligible; chat/empty states are not.
  const canToggleFullscreen = rightPanelOpen && !!activeReportType;

  // Keyboard: Esc exits, Cmd+\ / Ctrl+\ toggles while a supported panel is open.
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isReportPanelFullscreen) {
        e.preventDefault();
        exitFullscreen();
        return;
      }
      if (e.key === "\\" && (e.metaKey || e.ctrlKey) && canToggleFullscreen) {
        e.preventDefault();
        toggleReportPanelFullscreen();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [
    isReportPanelFullscreen,
    canToggleFullscreen,
    exitFullscreen,
    toggleReportPanelFullscreen,
  ]);

  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const handleTabChange = useCallback(
    (tab: DealTab) => {
      // Clicking a deal tab always returns to the normal split view.
      if (isReportPanelFullscreen) exitFullscreen();
      // A report viewer is scoped to the tab that opened it (e.g. opening
      // Deal Fit from Analysis). Switching tabs closes that report so the
      // new tab's content takes the full width. Chat is global to the deal
      // and stays open across tab changes.
      if (
        rightPanelOpen &&
        rightPanelContent &&
        typeof rightPanelContent === "object"
      ) {
        setRightPanelOpen(false);
        setRightPanelContent(null);
      }
      setActiveTab(tab);
      // Use the raw History API instead of setSearchParams: setSearchParams
      // cascades a router location update through every useLocation /
      // useSearchParams consumer, producing visible jank on each click even
      // though tab bodies are memoised. replaceState keeps the URL
      // shareable without notifying React Router.
      const url = new URL(window.location.href);
      url.searchParams.set("tab", tab);
      window.history.replaceState(null, "", url.toString());
      // Tabs share one scroll container; reset to top so a tall→short
      // switch doesn't leave scrollTop mid-content on the new tab.
      scrollContainerRef.current?.scrollTo({ top: 0, behavior: "instant" });
    },
    [isReportPanelFullscreen, exitFullscreen, rightPanelOpen, rightPanelContent]
  );

  const handleToggleRightPanel = useCallback(() => {
    if (rightPanelOpen) {
      setRightPanelOpen(false);
      setRightPanelContent(null);
      exitFullscreen();
    } else {
      setRightPanelOpen(true);
      setRightPanelContent("chat");
    }
  }, [rightPanelOpen, exitFullscreen]);

  const handleOpenInPanel = useCallback(
    (type: string, title: string, versionId?: string) => {
      // Opening (or switching) a report always starts in split view —
      // fullscreen is intentionally not remembered across open/close.
      exitFullscreen();
      setRightPanelContent({ type, title, versionId });
      setRightPanelOpen(true);
    },
    [exitFullscreen]
  );

  // String-typed wrapper around handleTabChange — kept stable so tabs that
  // receive it as a prop (Analysis) don't re-render on every parent render.
  const handleOpenAgentReport = useCallback(
    (templateId: string) => {
      setAgentReportRequest({ templateId, at: Date.now() });
      handleTabChange("analysis-docs");
    },
    [handleTabChange]
  );

  const handleNavigateToTab = useCallback(
    (tab: string, targetId?: string) => {
      handleTabChange(tab as DealTab);
      if (!targetId) return;
      // Target may not be mounted yet — wait a frame, then retry briefly.
      let attempts = 0;
      const flashWhenReady = () => {
        const el = document.getElementById(targetId);
        if (!el) {
          if (attempts++ < 10) window.setTimeout(flashWhenReady, 60);
          return;
        }
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        el.setAttribute("data-flash", "true");
        // A prior flash on this same element may still hold a pending
        // remove-timer; clearing it stops the old timer from yanking
        // data-flash off the fresh navigation mid-highlight.
        const prev = Number(el.dataset.flashTimer);
        if (prev) window.clearTimeout(prev);
        const t = window.setTimeout(() => {
          el.removeAttribute("data-flash");
          delete el.dataset.flashTimer;
        }, 2000);
        el.dataset.flashTimer = String(t);
      };
      window.requestAnimationFrame(flashWhenReady);
    },
    [handleTabChange]
  );
  if (!currentDeal) {
    return (
      <div className="flex h-full items-center justify-center text-muted-foreground">
        Deal not found
      </div>
    );
  }

  // Portco tab is portfolio-only: shown only when status is "Portfolio Companies".
  const isPortfolioCompany =
    currentDeal.overview.status === "Portfolio Companies";
  // A stale ?tab=portco on a non-portfolio deal would otherwise render an
  // empty content area — fall back to Profile so it's never blank.
  const effectiveTab: DealTab =
    activeTab === "portco" && !isPortfolioCompany ? "profile" : activeTab;

  return (
    <div className="flex h-full flex-col overflow-hidden bg-background">
      {/* Breadcrumb — hidden in fullscreen to maximise reading area. */}
      {!isReportPanelFullscreen && (
        <header className="flex h-12 items-center border-b border-border bg-card px-6">
          <nav className="flex items-center gap-1 text-sm">
            <Link
              to={`/${activeWorkspaceId}/deals`}
              className="text-muted-foreground transition-colors hover:text-foreground"
            >
              Pipeline
            </Link>
            <ChevronRight className="h-4 w-4 text-muted-foreground" />
            <span className="text-foreground">{currentDeal.overview.name}</span>
          </nav>
        </header>
      )}

      {/* Deal Header — hidden in fullscreen. */}
      {!isReportPanelFullscreen && (
        <DealHeaderV2
          currentDeal={currentDeal}
          onDelete={() => setDeleteOpen(true)}
          onInviteFounder={() => setInviteOpen(true)}
          invitationStatus={invitationStatus}
          onToggleAiChat={handleToggleRightPanel}
          isChatActive={rightPanelOpen && rightPanelContent === "chat"}
        />
      )}

      {/* Tab Bar — stays visible; clicking a tab exits fullscreen. */}
      <DealTabBar
        activeTab={effectiveTab}
        onTabChange={handleTabChange}
        showPortco={isPortfolioCompany}
        badges={{
          // Pending-review count scoped to fields the Profile tab
          // renders (shared helper with the in-tab ReviewHead so the
          // two counts stay identical).
          profile: currentDeal ? getProfilePendingCount(currentDeal) : 0,
        }}
      />

      {/* Main content area — split pane at lg+, drawer-style overlay below lg. */}
      <div className="relative flex flex-1 overflow-hidden">
        {/* Left: Tab content — hidden in fullscreen and also hidden below
            lg whenever the right panel is open (panel takes the full
            content area on phone/tablet per spec § 5.2). */}
        <div
          ref={scrollContainerRef}
          className={cn(
            "flex-1 overflow-y-auto transition-[width] duration-200 ease-out",
            isReportPanelFullscreen && "hidden",
            rightPanelOpen && !isReportPanelFullscreen && "hidden lg:block"
          )}
          style={
            rightPanelOpen && !isReportPanelFullscreen
              ? { width: `${100 - rightPanelWidth}%` }
              : undefined
          }
        >
          {/* All tabs mount on first load and stay mounted; switching toggles
              CSS visibility so tab changes are instant and tab-local effects
              and queries pay their setup cost once. */}
          <div className={cn(effectiveTab !== "profile" && "hidden")}>
            <ProfileTab
              currentDeal={currentDeal}
              reportStatus={reportStatus}
            />
          </div>
          <div className={cn(effectiveTab !== "analysis-docs" && "hidden")}>
            <AnalysisDocumentsTab
              currentDeal={currentDeal}
              active={effectiveTab === "analysis-docs"}
              onDocumentContextChange={setAnalysisContext}
              openRequest={agentReportRequest}
            />
          </div>
          <div className={cn(effectiveTab !== "analysis" && "hidden")}>
            <AnalysisTab
              currentDeal={currentDeal}
              reportStatus={reportStatus}
              onOpenInPanel={handleOpenInPanel}
              onNavigateToTab={handleNavigateToTab}
            />
          </div>
          <div className={cn(effectiveTab !== "reports" && "hidden")}>
            <ReportsTab
              currentDeal={currentDeal}
              reportStatus={reportStatus}
              onOpenInPanel={handleOpenInPanel}
              onOpenAgentReport={handleOpenAgentReport}
            />
          </div>
          <div className={cn(effectiveTab !== "data-room" && "hidden")}>
            <DataRoomTab currentDeal={currentDeal} />
          </div>
          <div className={cn(effectiveTab !== "emails" && "hidden")}>
            <EmailsTab currentDeal={currentDeal} />
          </div>
          <div className={cn(effectiveTab !== "notes" && "hidden")}>
            <NotesTab currentDeal={currentDeal} />
          </div>
          {/* Portco mounts only for portfolio companies so non-portfolio
              deals never pay the updates fetch. */}
          {isPortfolioCompany && (
            <div className={cn(effectiveTab !== "portco" && "hidden")}>
              <PortcoTab currentDeal={currentDeal} />
            </div>
          )}
        </div>

        {/* Right: Drag handle + Panel (chat or report viewer) */}
        <DealRightPanel
          isOpen={rightPanelOpen}
          widthPercent={rightPanelWidth}
          onWidthChange={setRightPanelWidth}
          fullscreen={isReportPanelFullscreen}
        >
          {(rightPanelContent === "chat" || rightPanelContent === null) && (
            <div className="relative h-full w-full">
              <button
                onClick={() => {
                  setRightPanelOpen(false);
                  setRightPanelContent(null);
                  exitFullscreen();
                }}
                className="absolute right-3 top-3 z-10 flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-foreground/[0.06] hover:text-foreground"
                aria-label="Close chat"
              >
                <X className="h-4 w-4" />
              </button>
              <DiligencegptSidebar
                side="right"
                collapsible="none"
                className="h-full w-full bg-card"
                currentDeal={currentDeal}
                documentContext={analysisContext}
              />
            </div>
          )}
          {rightPanelContent &&
            typeof rightPanelContent === "object" &&
            rightPanelContent.type && (
              <ReportPanelViewer
                type={rightPanelContent.type}
                title={rightPanelContent.title}
                versionId={rightPanelContent.versionId}
                currentDeal={currentDeal}
                reportStatus={reportStatus}
                maximized={isReportPanelFullscreen}
                onToggleMaximize={
                  canToggleFullscreen
                    ? toggleReportPanelFullscreen
                    : undefined
                }
                onClose={() => {
                  setRightPanelOpen(false);
                  setRightPanelContent(null);
                  exitFullscreen();
                }}
                onNavigateToTab={handleNavigateToTab}
              />
            )}
        </DealRightPanel>
      </div>

      <DeleteDialog
        isOpen={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        dealId={currentDeal.id}
        dealName={currentDeal.overview.name}
        workspaceId={activeWorkspaceId || ""}
        onSuccess={onDealDeleted}
      />

      <FounderInvitationDialog
        isOpen={inviteOpen}
        onClose={() => setInviteOpen(false)}
        companyId={currentDeal.id}
        companyName={currentDeal.overview.name}
        invitationStatus={invitationStatus}
        onInvitationUpdate={refetchInvitationStatus}
      />
    </div>
  );
}
